import { useState, useEffect, useCallback, useMemo } from "react";
import { useSingleProductUrlSync } from "./useSingleProductUrlSync";
import type {
  TransformedProduct,
  TransformedVariation,
  EnrichedVariationTransformed,
} from "../types/productApiTypes";

// Type for internal state: Attribute ID -> Selected Term ID
export type SelectedAttributeTermIds = Record<number, number>;

// Output type: Attribute Taxonomy Slug -> Selected Term Slug
export type SelectedAttributeSlugs = Record<string, string>;

export interface UseProductVariationSelectorResult {
  selectedAttributeSlugs: SelectedAttributeSlugs; // Output slugs for UI/URL
  selectedVariation: TransformedVariation | null; // The fully matched variation object
  handleAttributeSelect: (attributeName: string, valueSlug: string) => void; // Still accepts name/slug
  variationSelectionComplete: boolean; // Overall readiness flag
}

/**
 * Manages variation selection state using internal IDs for reliability,
 * synchronizes with URL using slugs, and leverages enriched product data.
 */
export const useProductVariationSelector = (
  product: TransformedProduct | null | undefined,
  // Full variations data fetched separately is still needed for price, stock, images etc.
  variations: TransformedVariation[] = [],
  shouldSync: boolean = true // Flag to enable/disable URL synchronization
): UseProductVariationSelectorResult => {
  // --- State ---
  const [selectedAttributeTermIds, setSelectedAttributeTermIds] =
    useState<SelectedAttributeTermIds>(() => {
      return {};
    });
  const [matchedVariationId, setMatchedVariationId] = useState<number | null>(
    () => {
      return null;
    }
  );
  const [initStatus, setInitStatus] = useState<
    "pending" | "running" | "complete"
  >(() => {
    return "pending";
  });
  const [currentProductId, setCurrentProductId] = useState<number | null>(
    () => {
      return null;
    }
  );
  const [variationSelectionComplete, setVariationSelectionComplete] =
    useState<boolean>(() => {
      return false;
    });

  // --- Memoized Lookups & Derived Data ---
  // Create lookup maps only when product.enrichedAttributes changes
  const {
    attributeNameToIdMap,
    attributeIdToTaxonomyMap,
    attributeTaxonomyToIdMap,
    termMaps, // Nested maps: { attributeId: { slug: termId, id: termSlug } }
  } = useMemo(() => {
    const idToName: Record<number, string> = {};
    const nameToId: Record<string, number> = {};
    const idToTaxonomy: Record<number, string> = {};
    const taxonomyToId: Record<string, number> = {};
    const termLookups: Record<
      number,
      { slugToId: Record<string, number>; idToSlug: Record<number, string> }
    > = {};

    if (product?.enrichedAttributes) {
      product.enrichedAttributes.forEach((attr) => {
        if (attr.hasVariations && attr.taxonomy) {
          idToName[attr.id] = attr.name;
          nameToId[attr.name] = attr.id;
          idToTaxonomy[attr.id] = attr.taxonomy;
          taxonomyToId[attr.taxonomy] = attr.id;

          const slugMap: Record<string, number> = {};
          const idMap: Record<number, string> = {};
          attr.terms.forEach((term) => {
            slugMap[term.slug] = term.id;
            idMap[term.id] = term.slug;
          });
          termLookups[attr.id] = { slugToId: slugMap, idToSlug: idMap };
        }
      });
    }
    const result = {
      attributeIdToNameMap: idToName,
      attributeNameToIdMap: nameToId,
      attributeIdToTaxonomyMap: idToTaxonomy,
      attributeTaxonomyToIdMap: taxonomyToId,
      termMaps: termLookups,
    };
    return result;
  }, [product?.id, product?.enrichedAttributes]); // Recalculate only if attributes change

  // Derive the slug-based map needed for URL sync and potentially UI
  const selectedAttributeSlugs = useMemo<SelectedAttributeSlugs>(() => {
    const slugMap: SelectedAttributeSlugs = {};
    Object.entries(selectedAttributeTermIds).forEach(([attrIdStr, termId]) => {
      const attrId = parseInt(attrIdStr, 10);
      const taxonomy = attributeIdToTaxonomyMap[attrId];
      const termSlug = termMaps[attrId]?.idToSlug[termId];
      if (taxonomy && termSlug) {
        slugMap[taxonomy] = termSlug;
      } else {
        console.warn(
          `[VS] MEMO: Could not derive slug for attrId ${attrId}, termId ${termId}. Taxonomy: ${taxonomy}, TermSlug: ${termSlug}`
        );
      }
    });
    return slugMap;
  }, [selectedAttributeTermIds, attributeIdToTaxonomyMap, termMaps]);

  // --- URL Sync ---
  // Pass the derived slug map to the URL sync hook
  const { urlAttributes, syncComplete } = useSingleProductUrlSync(
    selectedAttributeSlugs,
    shouldSync ? product : null, // Pass product only if syncing
    variationSelectionComplete // <-- Pass the flag
  );

  // Determine overall completion status
  useEffect(() => {
    const isComplete = syncComplete && initStatus === "complete";
    if (isComplete !== variationSelectionComplete) {
      setVariationSelectionComplete(isComplete);
    }
  }, [syncComplete, initStatus, variationSelectionComplete]);

  // --- Find Matching Variation ID Logic ---
  const findMatchingVariationId = useCallback(
    (
      selectedIds: SelectedAttributeTermIds
    ): EnrichedVariationTransformed | null => {
      const enrichedVariations = product?.enrichedVariations || [];
      if (!enrichedVariations.length) {
        return null;
      }

      const selectedAttributeIds = Object.keys(selectedIds).map(Number);

      // Count variation attributes defined on the parent product
      const totalVariationAttributes =
        product?.enrichedAttributes?.filter((attr) => attr.hasVariations)
          .length || 0;

      // If the user hasn't selected a value for every variation attribute, no match is possible yet
      if (selectedAttributeIds.length !== totalVariationAttributes) {
        return null;
      }

      // Find the enriched variation definition that matches the selected IDs
      const matchedEnrichedVariation = enrichedVariations.find(
        (enrichedVar) => {
          // Quick check: must have the same number of attributes
          if (enrichedVar.attributes.length !== selectedAttributeIds.length) {
            return false;
          }
          // Deep check: every selected ID pair must exist in this enriched variation's attributes
          const allMatch = selectedAttributeIds.every((selectedAttrId) => {
            const selectedTermId = selectedIds[selectedAttrId];
            // Find the attribute within this enriched variation
            const varAttr = enrichedVar.attributes.find(
              (va) => va.id === selectedAttrId
            );
            // Check if the attribute exists and its (single) term ID matches the selection
            const termMatch =
              varAttr && varAttr.terms[0]?.id === selectedTermId;
            return termMatch;
          });
          return allMatch;
        }
      );

      return matchedEnrichedVariation || null;
    },
    [product?.enrichedVariations, product?.enrichedAttributes] // Dependencies
  );

  // --- Handle Attribute Selection Callback ---
  const handleAttributeSelect = useCallback(
    (attributeName: string, termSlug: string): void => {
      if (initStatus !== "complete") {
        setInitStatus("complete"); // Mark init as done if user interacts
      }
      const attributeId = attributeNameToIdMap[attributeName];
      if (attributeId === undefined) {
        console.warn(
          `[VS] handleAttributeSelect: Attribute ID not found for name: ${attributeName}`
        );
        return;
      }
      const termId = termMaps[attributeId]?.slugToId[termSlug];

      if (termId === undefined && termSlug !== "") {
        // Only warn if a specific slug was chosen but not found
        console.warn(
          `[VS] handleAttributeSelect: Term ID not found for attribute "${attributeName}" (ID: ${attributeId}), slug: "${termSlug}"`
        );
      }

      setSelectedAttributeTermIds((prev) => {
        const newState = { ...prev }; // Create a copy
        if (termSlug === "") {
          // If clearing selection, delete the key from the state
          delete newState[attributeId];
        } else if (termId !== undefined) {
          // If setting a selection, and termId is valid, assign it
          newState[attributeId] = termId;
        }
        return newState; // Return the modified or unmodified state
      });
    },
    [initStatus, attributeNameToIdMap, termMaps]
  );

  // --- Effect to Update Matched Variation ID ---
  useEffect(() => {
    // Filter out undefined values before matching
    const validSelections: SelectedAttributeTermIds = {};
    Object.entries(selectedAttributeTermIds).forEach(([key, value]) => {
      if (value !== undefined) {
        validSelections[Number(key)] = value;
      }
    });

    const matchedEnrichedVar = findMatchingVariationId(validSelections);
    const newMatchedId = matchedEnrichedVar?.id ?? null;

    if (newMatchedId !== matchedVariationId) {
      setMatchedVariationId(newMatchedId);
    }
  }, [selectedAttributeTermIds, findMatchingVariationId, matchedVariationId]);

  // --- Derive Selected Variation Object ---
  const selectedVariation = useMemo(() => {
    if (!matchedVariationId || !variations || variations.length === 0) {
      return null;
    }
    // Find the full variation data using the matched ID
    const foundVariation =
      variations.find((v) => v.id === matchedVariationId) || null;
    return foundVariation;
  }, [matchedVariationId, variations]);

  // --- Effect to Reset State on Product Change ---
  useEffect(() => {
    const newProductId = product?.id ?? null;
    if (newProductId !== currentProductId) {
      setSelectedAttributeTermIds({});
      setMatchedVariationId(null);
      setCurrentProductId(newProductId);
      setInitStatus("pending"); // Reset init status for the new product
      setVariationSelectionComplete(false); // Reset overall completion
    }
  }, [product?.id, currentProductId]);

  // --- Initialization Effect (URL -> Default -> First) ---
  useEffect(() => {
    if (
      !syncComplete ||
      !product?.id ||
      !product.enrichedAttributes ||
      initStatus !== "pending" ||
      product.id !== currentProductId
    ) {
      return; // Conditions not met for initialization
    }

    setInitStatus("running");

    const initialIdState: SelectedAttributeTermIds = {};
    let appliedSource: "url" | "default" | "first" | "none" = "none";

    // 1. Try URL attributes (mapping slugs to IDs)
    if (shouldSync && Object.keys(urlAttributes).length > 0) {
      let urlApplied = false;
      Object.entries(urlAttributes).forEach(([taxonomySlug, termSlug]) => {
        const attributeId = attributeTaxonomyToIdMap[taxonomySlug];
        const termId = termMaps[attributeId]?.slugToId[termSlug];
        if (attributeId !== undefined && termId !== undefined) {
          initialIdState[attributeId] = termId;
          urlApplied = true;
        }
      });
      if (urlApplied) {
        appliedSource = "url";
      }
    }

    // 2. Try Default Variation (using IDs)
    if (
      appliedSource === "none" &&
      product.hasDefaultVariation &&
      product.defaultVariationData?.id
    ) {
      // Find the default variation's enriched data
      const defaultEnrichedVar = product.enrichedVariations?.find(
        (ev) => ev.id === product.defaultVariationData?.id
      );
      if (defaultEnrichedVar) {
        let defaultApplied = false;
        defaultEnrichedVar.attributes.forEach((varAttr) => {
          const termId = varAttr.terms[0]?.id; // Get term ID from the single term present
          if (termId !== undefined) {
            initialIdState[varAttr.id] = termId; // varAttr.id is the attributeId
            defaultApplied = true;
          }
        });
        if (defaultApplied) {
          appliedSource = "default";
        }
      }
    }

    // 3. Try First Available Option (using IDs)
    if (appliedSource === "none") {
      let firstApplied = false;
      product.enrichedAttributes?.forEach((attr) => {
        if (attr.hasVariations && attr.terms.length > 0) {
          const firstTermId = attr.terms[0].id;
          initialIdState[attr.id] = firstTermId;
          firstApplied = true;
        }
      });
      if (firstApplied) {
        appliedSource = "first";
      }
    }

    // Set the internal ID state
    if (appliedSource !== "none") {
      setSelectedAttributeTermIds(initialIdState);
    } else if (Object.keys(selectedAttributeTermIds).length > 0) {
      // Clear state if no source applied and state wasn't already empty
      setSelectedAttributeTermIds({});
    }

    setInitStatus("complete"); // Mark initialization complete
  }, [
    syncComplete,
    product, // product object includes enriched data
    urlAttributes,
    shouldSync,
    initStatus,
    currentProductId,
    attributeTaxonomyToIdMap, // Include maps used in logic
    termMaps,
    selectedAttributeTermIds, // Include to compare before clearing
  ]);

  return {
    selectedAttributeSlugs, // Return the derived slug map
    selectedVariation,
    handleAttributeSelect,
    variationSelectionComplete,
  };
};
